import type { UseComboboxStateChange } from 'downshift';
import { isEqual } from 'lodash-es';
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, type KeyboardEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import { useIsPopout } from '../../../hooks/useCurrentDocument';
import { useDynamicCallback } from '../../../hooks/useDynamicCallback';
import { usePageVisibility } from '../../../hooks/usePageVisibility';
import { EMPTY_OBJECT } from '../../../utils/empty';
import type { RenderResultFunc } from '../Autocomplete/types';
import { AutocompleteDropdown, AutocompleteResult } from '../AutocompleteDropdown';
import type { AutocompleteDropdownProps } from '../AutocompleteDropdown/types';
import { BaseSelect } from '../BaseSelect';
import { useDropdownPopper } from '../Dropdown/useDropdownPopper';
import { Input } from '../Input';
import { FormControlSizes } from '../types';
import { SearchSelectWrapper } from './styles';
import type { SearchSelectProps, SearchSelectRef } from './types';
import { useSearchSelect } from './useSearchSelect';
import {
  shouldSkipSearchSelectItemTooltip,
  wrapSearchSelectResultWithTooltip,
} from './wrapSearchSelectResultWithTooltip';

const messages = defineMessages({
  search: {
    defaultMessage: 'Search...',
    id: 'Form.SearchSelect.search',
  },
});

export function getStringLabel(item: string) {
  return item;
}

/** Warning - be careful NOT to confuse this with **SymbolSelector** - this is specifically for generic dropdowns */
const SearchSelectInner = <T,>(props: SearchSelectProps<T>, ref: React.ForwardedRef<SearchSelectRef<T>>) => {
  const { baseSize } = useTheme();
  const {
    id,
    options,
    selection,
    getLabel,
    getDescription,
    onChange,
    onBlur,
    isItemDisabled,
    disabled = false,
    readOnly = false,
    placeholder,
    searchPlaceholder,
    showClear = false,
    initialSortByLabel = true,
    equalityChecker = isEqual,
    prefix,
    suffix,
    size = FormControlSizes.Default,
    invalid,
    touched,
    dropdownContentRef: incomingDropdownContentRef,
    dropdownWidth,
    dropdownSize = props.size || FormControlSizes.Default,
    dropdownPlacement = 'bottom',
    variant,
    portalize,
    autoFocus,
    getGroup,
    getTabGroup,
    maxHeight,
    groupMaxHeight,
    groupSorter,
    RenderGroupHeader,
    renderResult,
    getItemTooltip,
    itemTooltipPlacement = 'left-start',
    showDropdownSearch = true,
    itemSize = getDropdownItemHeight(baseSize, dropdownSize),
    initialIsOpen,
    selectorStyle,
    showDescriptionInButton,
    getButtonDescription,
    gridTemplateColumns,
    showTitle,
    onIsOpenChange,
    dropdownSuffix,
    centered,
    showChevron,
    searchPrefix,
    onClearClick,
    onInputValueChange,
    onStateChange,
    additionalSearchMatcher,
    sortFilterOverride,
    scrollSelectionIntoViewIndex,
    customButtonLayout,
    showGroupTabs,
    suppressGroupTabsOverflow,
    dataTestID,
    ...restOfProps
  } = props;

  const dropdownReferenceElement = useRef<HTMLLabelElement | null>(null);
  const defaultDropdownContentRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = incomingDropdownContentRef ?? defaultDropdownContentRef;
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOpenChange = useDynamicCallback((changes: UseComboboxStateChange<T>) => {
    if (changes.isOpen == null) {
      return;
    }

    onIsOpenChange?.(Object.assign(changes, { isOpen: changes.isOpen }));

    if (changes.isOpen && showDropdownSearch) {
      inputRef.current?.focus();
    } else if (!changes.isOpen) {
      dropdownReferenceElement.current?.focus();
    }
  });

  const searchSelect = useSearchSelect({
    id,
    selection,
    items: options,
    onInputValueChange,
    onStateChange,
    inputRef,
    initialSortByLabel,
    onChange,
    getLabel,
    getDescription,
    isItemDisabled,
    getGroup,
    getTabGroup,
    groupSorter,
    itemSize,
    onIsOpenChange: handleOpenChange,
    initialIsOpen,
    additionalSearchMatcher,
    sortFilterOverride,
    scrollSelectionIntoViewIndex,
  });
  const { reset, getInputProps, isOpen, openMenu, closeMenu } = searchSelect;

  const isPopout = useIsPopout();
  const isVisible = usePageVisibility();
  const disableAnimations = isPopout && !isVisible;

  const { formatMessage } = useIntl();
  useImperativeHandle(ref, () => {
    return { openMenu };
  }, [openMenu]);

  const dropdownPopper = useDropdownPopper({
    isOpen,
    dropdownWidth,
    dropdownPlacement,
    referenceElement: dropdownReferenceElement.current ?? null,
    dropdownContentRef,
  });

  const handleClearClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      reset();
      e.preventDefault();

      // We allow the implementer to handle the clear click event as they wish.
      if (onClearClick) {
        onClearClick(e);
      } else {
        onChange(undefined);
      }
    },
    [reset, onChange, onClearClick]
  );

  // React 18 timing synchronizes state changes, so we (sometimes) need to wait a tick for isOpen to get changed
  // - This was needed to certain auto-opening (double) dropdowns worked as expected (https://github.com/talostrading/Ava-UI/pull/6271#discussion_r1667075934)
  // - TODO: this should be rewritten to avoid the in-render ref change, but it fixes this for now
  const isOpenRef = useRef(isOpen);
  if (isOpenRef.current !== isOpen) {
    isOpenRef.current = isOpen;
  }
  const handleSelectClick = useDynamicCallback(() => {
    if (disableAnimations) {
      if (!isOpenRef.current && !readOnly && !disabled) {
        openMenu();
      }
      return;
    }

    requestAnimationFrame(() => {
      if (!isOpenRef.current && !readOnly && !disabled) {
        openMenu();
      }
    });
  });

  const handleSelectKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // If the user is focusing the select and starts typing alpha numeric chars, open the dropdown for them
      if (/\b[a-zA-Z0-9]\b/.test(e.key) && !isOpen) {
        handleSelectClick();
      } else if (e.key === 'Enter' || e.key === ' ') {
        // Prevent default here, otherwise the potential space click can cause the search input field to start with a space entered
        e.preventDefault();
        handleSelectClick();
      }
    },
    [handleSelectClick, isOpen]
  );

  const handleSelectKeyDownCapture = useDynamicCallback((e: KeyboardEvent) => {
    // The dropdown content keeps its own Escape handling (including downshift's reset), and portalized content
    // still reaches this handler through the React tree, so only close from the trigger itself.
    const isFromDropdownContent = e.target instanceof Node && dropdownContentRef.current?.contains(e.target);
    if (e.key === 'Escape' && isOpenRef.current && !isFromDropdownContent) {
      // Match dropdown-content Escape: close this menu without dismissing a parent modal or drawer.
      e.stopPropagation();
      closeMenu();
      dropdownReferenceElement.current?.focus();
    }
  });

  const handleDropdownKeyDown = useDynamicCallback((e: KeyboardEvent<HTMLElement>) => {
    // We have to catch and stop the dropdown key events from propagating and hitting the SearchSelectWrapper
    // onKeyDown event handlers
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
    }

    if (e.key === 'Escape') {
      // Escape closes the dropdown when open, letting parent containers handle it when closed
      if (isOpen) {
        e.stopPropagation();
        return;
      }

      // Return focus to trigger if stuck in hidden dropdown content
      dropdownReferenceElement.current?.focus();
    }
  });

  const handleBlur = useDynamicCallback((e: React.FocusEvent<HTMLDivElement, Element>) => {
    // Check if the target focus is being shifted to is either the search select wrapper, is within the wrapper,
    // or is within the dropdown (the dropdown might be portalized so we have to explicitly check that too)
    if (
      e.relatedTarget === inputRef.current ||
      inputRef.current?.contains(e.relatedTarget) ||
      dropdownContentRef.current?.contains(e.relatedTarget)
    ) {
      return;
    }

    onBlur?.();
  });

  const initialRender = useRef(true);
  useEffect(() => {
    if (autoFocus && initialRender.current) {
      openMenu();
      initialRender.current = false;
    }
  }, [autoFocus, openMenu]);

  const defaultRenderResult: AutocompleteDropdownProps<T>['renderResult'] = useCallback(
    (item, disabled, searchString) => {
      // AutocompleteResult sets title={label} for truncation. Omit the native title only when a rich
      // tooltip will wrap this row, so skipped items keep the truncation fallback.
      const itemTooltip = getItemTooltip?.(item.item.item);
      return AutocompleteResult(item, disabled, searchString, undefined, {
        nativeTitle: shouldSkipSearchSelectItemTooltip(itemTooltip),
      });
    },
    [getItemTooltip]
  );

  const renderResultWithItemTooltip: RenderResultFunc<T> = useCallback(
    (searchResult, disabled, searchString) => {
      const result = (renderResult ?? defaultRenderResult)(searchResult, disabled, searchString);
      if (getItemTooltip == null) {
        return result;
      }
      const itemTooltip = getItemTooltip(searchResult.item.item);
      return wrapSearchSelectResultWithTooltip(result, itemTooltip, {
        placement: itemTooltipPlacement,
        size: dropdownSize,
      });
    },
    [defaultRenderResult, dropdownSize, getItemTooltip, itemTooltipPlacement, renderResult]
  );

  return (
    <SearchSelectWrapper
      {...restOfProps}
      onKeyDown={handleSelectKeyDown}
      onKeyDownCapture={handleSelectKeyDownCapture}
      data-value={selection ? getLabel(selection) : ''}
      data-disabled={disabled}
    >
      <BaseSelect
        id={id}
        isDropdownOpened={isOpen}
        value={selection}
        disabled={disabled}
        readOnly={readOnly}
        prefix={prefix}
        size={size}
        invalid={invalid}
        touched={touched}
        autoFocus={autoFocus}
        placeholder={placeholder}
        wrapperRef={dropdownReferenceElement}
        onClick={handleSelectClick}
        getLabel={getLabel}
        variant={variant}
        clearable={showClear}
        onClearClick={handleClearClick}
        suffix={suffix}
        style={selectorStyle}
        customButtonLayout={customButtonLayout}
        showDescriptionHeight={showDescriptionInButton && customButtonLayout == null}
        getDescription={showDescriptionInButton ? (getButtonDescription ?? getDescription) : undefined}
        gridTemplateColumns={gridTemplateColumns}
        showTitle={showTitle}
        centered={centered}
        showChevron={showChevron}
        dataTestID={dataTestID}
      />
      <AutocompleteDropdown
        id={`dropdown-${id}`}
        {...searchSelect}
        {...dropdownPopper}
        renderResult={renderResultWithItemTooltip}
        RenderGroupHeader={RenderGroupHeader}
        maxHeight={maxHeight}
        groupMaxHeight={groupMaxHeight}
        equalityChecker={equalityChecker}
        portalize={portalize}
        size={dropdownSize}
        onKeyDown={handleDropdownKeyDown}
        suffixCb={dropdownSuffix}
        showGroupTabs={showGroupTabs}
        suppressGroupTabsOverflow={suppressGroupTabsOverflow}
        childrenAboveResults={
          <Input
            // hide the input field if we don't want to do searching. We still have to render it due to downshift requirements to set the inputRef
            style={showDropdownSearch ? EMPTY_OBJECT : { display: 'none' }}
            size={dropdownSize}
            {...getInputProps({ disabled, ref: inputRef, onBlur: handleBlur })}
            placeholder={searchPlaceholder ?? formatMessage(messages.search)}
            disabled={disabled}
            readOnly={readOnly}
            prefix={searchPrefix}
          />
        }
      />
    </SearchSelectWrapper>
  );
};

/**
 * Dropdown component (using downshiftjs) that allows for searching and selecting items from a list of options.
 * - has support for custom tab-based groups
 *
 * Warning: Be careful NOT to confuse this with **SymbolSelector**, which uses useSearchSelect internally and sounds similar - it's related but not the same */
export const SearchSelect = memo(forwardRef(SearchSelectInner)) as <T>(
  props: SearchSelectProps<T> & { ref?: React.ForwardedRef<SearchSelectRef> }
) => ReturnType<typeof SearchSelectInner>;

/** This function calculates the search select's item size. Its kinda arbitrary so this is being exposed so the logic can be reused in other implementations */
export function getDropdownItemHeight(baseSize: number, dropdownSize: number) {
  return (baseSize * 2.5 * dropdownSize) / 2;
}
